/**
 * Chronology entry helpers — date grouping + citation links.
 *
 * Shared pure functions for the Chronology Builder canvas. Date formatting +
 * grouping mirror components/TimelineCard.jsx so the curated canvas and the
 * read-only timeline render dates identically. Citation links reuse
 * domain/sourceRef.js so clicking an entry's source opens the global document
 * viewer overlay exactly like chat citations.
 */

import { buildEvidenceUrl, buildEvidenceLabel } from './sourceRef';

/** Parse an ISO value to a Date rendering its LITERAL date/time.
 *
 * Chronology dates are case-local by convention: the parser stores the
 * document's own clock time ("03-JAN-2025 0208" → 02:08) with UTC attached
 * purely for storage. So we must never shift into the browser's timezone —
 * that rendered the incident above as "Jan 2, 7:08 PM" for US users, and
 * `new Date('2022-06-17')` has the same prior-day problem for calendar
 * dates. Build the Date from the ISO parts, ignoring any zone suffix.
 * Returns null for an unparseable value.
 */
function localDate(iso, _dateOnly) {
  if (!iso) return null;
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    return new Date(
      Number(m[1]), Number(m[2]) - 1, Number(m[3]),
      Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0),
    );
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Pretty-print an ISO date as a date header, honest to its precision:
 *  day → "March 5, 2024"; month → "March 2024"; year → "2024". */
export function formatDateHeader(iso, precision = 'day') {
  if (!iso) return 'Undated';
  const d = localDate(iso, true);
  if (!d) return String(iso).slice(0, 10);
  if (precision === 'year') return d.toLocaleDateString(undefined, { year: 'numeric' });
  if (precision === 'month') {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  }
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

/** Short date label for an ISO value (precision- and date-only-aware). */
export function formatDate(iso, dateOnly = true, precision = 'day') {
  if (!iso) return '';
  const d = localDate(iso, dateOnly);
  if (!d) return String(iso).slice(0, 10);
  if (precision === 'year') return d.toLocaleDateString(undefined, { year: 'numeric' });
  if (precision === 'month') {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  }
  if (dateOnly) {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Short date label for an entry row (precision- and date-only-aware). */
export function formatEntryDate(entry) {
  return formatDate(entry.effective_date, entry.date_only, entry.date_precision);
}

/** Bucket key for a dated entry — precision-aware so month/year partial
 *  dates group at their own bucket instead of a falsely-exact day. */
function bucketKey(e) {
  const iso = String(e.effective_date);
  if (e.date_precision === 'year') return `${iso.slice(0, 4)}-Y`;
  if (e.date_precision === 'month') return `${iso.slice(0, 7)}-M`;
  return iso.slice(0, 10);
}

/** Group entries for display, PRESERVING SERVER ORDER.
 *
 *  The server's order_entries() is the one ordering function (chronological,
 *  position as intra-day sequence, pinned undated entries in-flow); this
 *  helper only derives day-group headers from that order and separates the
 *  tray. Pinned undated entries ride inside the group they were placed in —
 *  a pinned-before entry is buffered so it lands under its ANCHOR's header,
 *  not the previous day's. Dismissed entries are excluded unless
 *  `includeDismissed` is set. */
export function partitionEntries(entries, { includeDismissed = false } = {}) {
  const live = (entries || []).filter(
    (e) => includeDismissed || e.review_state !== 'dismissed',
  );
  // Tray: server marks placement; fall back to "undated and unpinned" for
  // responses predating the placement field.
  const isTray = (e) => !e.effective_date
    && (e.placement ? e.placement === 'tray' : !e.anchor_entry_id);
  const undated = live.filter(isTray);
  const inFlow = live.filter((e) => !isTray(e));

  // Containment tree: children render nested under their parent (broad
  // left, specific right — horizontal position means containment and
  // nothing else). Server order defines sibling sequence; groups/day
  // headers derive from TOP-LEVEL entries only.
  const flowIds = new Set(inFlow.map((e) => e.id));
  const childrenOf = {};
  const flow = [];
  for (const e of inFlow) {
    const pid = e.parent_entry_id;
    if (pid && pid !== e.id && flowIds.has(pid)) {
      (childrenOf[pid] = childrenOf[pid] || []).push(e);
    } else {
      flow.push(e);
    }
  }

  const groups = [];
  let current = null;
  let buffered = [];   // pinned-before entries awaiting their anchor's group
  for (const e of flow) {
    if (e.effective_date) {
      const key = bucketKey(e);
      if (!current || current.key !== key) {
        current = { key, label: formatDateHeader(e.effective_date, e.date_precision), items: [] };
        groups.push(current);
      }
      if (buffered.length) { current.items.push(...buffered); buffered = []; }
      current.items.push(e);
    } else if (e.anchor_side === 'before') {
      buffered.push(e);
    } else if (current) {
      current.items.push(e);
    } else {
      buffered.push(e);
    }
  }
  if (buffered.length) {
    if (current) current.items.push(...buffered);
    else groups.push({ key: '__pinned__', label: 'Placed', items: buffered });
  }
  return { groups, undated, childrenOf };
}

/** Build a SourceRef for an entry's first source document, or null. */
export function entrySourceRef(entry) {
  const docId = (entry.source_document_ids || [])[0];
  if (!docId) return null;
  return {
    document_id: docId,
    chunk_id: (entry.source_chunk_ids || [])[0],
    snippet: entry.title || entry.description || '',
    kind: 'none',
    verified: true,
  };
}

/** Citation href + label for an entry (or null if no linkable source). */
export function entryCitation(entry, pathname) {
  const ref = entrySourceRef(entry);
  if (!ref) return null;
  const href = buildEvidenceUrl(ref, pathname);
  if (!href) return null;
  return { href, label: buildEvidenceLabel(ref) || 'Open source' };
}

/** Display name for one participant (string or {name/canonical_name} shape). */
export function participantName(p) {
  if (!p) return '';
  if (typeof p === 'string') return p;
  return p.name || p.canonical_name || '';
}
